import {
  CheckIcon,
  PaperClipIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import { useMutation } from "@tanstack/react-query"
import { Tooltip } from "antd"
import { lazy, Suspense, useEffect, useMemo, useState } from "react"
import { newArchAxiosInstance } from "utils/request"
import { onQueryError } from "utils/utils"
import { KREvidenceSummary } from "pages/dashboard/performance/okrs/interface/department"
import type { EvidenceRole, KREvidenceData } from "./KeyResultEvidenceDrawer"
import { useAppNotificationStore } from "stores"

// Code-split: the drawer is only mounted once the user opens it.
const KeyResultEvidenceDrawer = lazy(() => import("./KeyResultEvidenceDrawer"))

interface Props {
  keyResultId: number
  reviewPeriodId: number
  keyResultTitle: string
  role: EvidenceRole
  viewType?: "edit" | "view"
  requireEvidence?: boolean
  evidenceSummary?: KREvidenceSummary
  onSatisfactionChange?: (keyResultId: number, satisfied: boolean) => void
  onEvidenceSaved?: () => void
}

const isSummarySatisfied = (s: KREvidenceSummary | null | undefined) => {
  if (!s) return false
  if (s.hasEvidence === false) return true
  if (s.imageCount > 0) return true
  if (s.hasComment) return true
  return false
}

const summarizeFull = (data: KREvidenceData): KREvidenceSummary => ({
  hasEvidence: data.hasEvidence,
  imageCount: data.imageUrls?.length || 0,
  hasComment: !!(data.comment && data.comment.trim().length > 0),
})

/**
 * Inline evidence control rendered on every KR row.
 *
 * Employee + edit mode → "Have evidence? [Yes] [No]" segmented pills.
 *   - Yes opens the drawer so they can attach images / comment.
 *   - No POSTs `HasEvidence: false` directly (no drawer needed).
 *
 * Everyone else (manager, admin, scribe, ARC, HOD, or employee in view
 * mode) → paperclip icon that opens the drawer in read-only mode. The
 * paperclip is hidden when there is no evidence and evidence is not
 * required.
 *
 * The row no longer fetches its own evidence — `evidenceSummary` comes
 * from the parent KR listing. Local optimistic state covers the gap
 * between a save and the listing refetch.
 */
const KeyResultEvidenceButton = ({
  keyResultId,
  reviewPeriodId,
  keyResultTitle,
  role,
  viewType = "edit",
  requireEvidence = false,
  evidenceSummary,
  onSatisfactionChange,
  onEvidenceSaved,
}: Props) => {
  const [open, setOpen] = useState(false)

  // Local override so the row reflects a save (or inline "No") instantly,
  // without waiting for the parent listing query to refetch.
  const [localSummary, setLocalSummary] = useState<KREvidenceSummary | null>(
    null
  )
  const { toast } = useAppNotificationStore()

  // Drop the optimistic override once the parent listing has refetched
  // and given us a fresh summary — otherwise `localSummary` keeps masking
  // the source of truth and stale/wrong optimistic state gets stuck.
  useEffect(() => {
    setLocalSummary(null)
  }, [evidenceSummary])

  const current = localSummary ?? evidenceSummary ?? null
  const satisfied = isSummarySatisfied(current)

  const count = useMemo(() => {
    if (!current) return 0
    if (!current.hasEvidence) return 0
    return current.imageCount + (current.hasComment ? 1 : 0)
  }, [current])

  // Bubble satisfaction state up so the OKR step can gate "Next".
  useEffect(() => {
    onSatisfactionChange?.(keyResultId, satisfied)
  }, [keyResultId, satisfied, onSatisfactionChange])

  const { isLoading: markingNoEvidence, mutate: markNoEvidence } = useMutation({
    mutationFn: async () => {
      const formData = new FormData()
      formData.append("Comment", "")
      formData.append("HasEvidence", "false")
      const res = await newArchAxiosInstance.post(
        `/api/v2/employee/okr/evidence/${reviewPeriodId}/${keyResultId}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      )
      return res.data
    },
    onSuccess: () => {
      setLocalSummary({ hasEvidence: false, imageCount: 0, hasComment: false })
      onEvidenceSaved?.()
      toast.success("Marked: no evidence")
    },
    onError: onQueryError,
  })

  const handleDrawerSave = (state: KREvidenceData) => {
    setLocalSummary(summarizeFull(state))
    onEvidenceSaved?.()
  }

  const isEmployeeEdit = role === "employee" && viewType === "edit"

  // ─── Manager / read-only view ───────────────────────────────────────────
  if (!isEmployeeEdit) {
    const isManagerView = role !== "employee"
    if (
      isManagerView &&
      count === 0 &&
      current?.hasEvidence !== false &&
      !requireEvidence
    ) {
      return null
    }

    const showCountBadge = count > 0
    const showNoEvidenceDot = current?.hasEvidence === false

    const tooltipTitle =
      current?.hasEvidence === false
        ? "Employee: no evidence"
        : count > 0
        ? `${count} attachment${count === 1 ? "" : "s"}`
        : "View evidence"

    return (
      <>
        <Tooltip
          title={tooltipTitle}
          placement="top"
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setOpen(true)
            }}
            aria-label={tooltipTitle}
            className={`
              relative inline-flex items-center justify-center
              w-8 h-8 rounded-full transition-colors shrink-0
              ${
                count > 0
                  ? "bg-[#D6E0F8] text-primary500 hover:bg-[#C5D3F4]"
                  : "bg-[#F3F5FB99] text-padeSubtext hover:bg-[#EEF0F7]"
              }
            `}
          >
            <PaperClipIcon className="w-4 h-4" />

            {showCountBadge && (
              <span
                className="
                  absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full
                  text-[10px] font-bold flex items-center justify-center
                  ring-2 ring-white bg-primary500 text-white
                "
              >
                {count}
              </span>
            )}

            {!showCountBadge && showNoEvidenceDot && (
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-padeSubtext border-2 border-white" />
            )}
          </button>
        </Tooltip>

        {open && <Suspense fallback={null}><KeyResultEvidenceDrawer
          open={open}
          onClose={() => setOpen(false)}
          keyResultId={keyResultId}
          reviewPeriodId={reviewPeriodId ?? 0}
          keyResultTitle={keyResultTitle}
          role={role}
          viewType={viewType}
          requireEvidence={requireEvidence}
          onEvidenceChange={handleDrawerSave}
        /></Suspense>}
      </>
    )
  }

  // ─── Employee + edit mode: "Have evidence? [Yes] [No]" ────────────────
  const yesSelected = !!current && current.hasEvidence === true && count > 0
  const noSelected = current?.hasEvidence === false
  const requiredUnanswered = requireEvidence && !yesSelected && !noSelected

  return (
    <>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setOpen(true)
          }}
          className="text-primary500 font-circular text-sm font-medium hover:underline"
        >
          Have evidence?
        </button>

        <div className="flex items-center gap-1.5">
          <Tooltip
            title="Attach images or a comment"
            placement="top"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setOpen(true)
              }}
              disabled={markingNoEvidence}
              className={`
                inline-flex items-center gap-1 px-2.5 py-1 rounded-full
                text-xs font-medium font-circular transition-colors
                ${
                  yesSelected
                    ? "bg-[#D6E0F8] text-primary500 ring-1 ring-primary500"
                    : requiredUnanswered
                    ? "bg-[#F3F5FB] text-padeBlack ring-1 ring-[#F59E0B] hover:bg-[#EEF0F7]"
                    : "bg-[#F3F5FB] text-padeBlack hover:bg-[#EEF0F7]"
                }
              `}
            >
              <CheckIcon className="w-3.5 h-3.5" /> Yes
              {yesSelected && count > 0 ? (
                <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold bg-primary500 text-white">
                  {count}
                </span>
              ) : null}
            </button>
          </Tooltip>

          <Tooltip
            title="I have no evidence for this key result"
            placement="top"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                if (markingNoEvidence) return
                markNoEvidence()
              }}
              disabled={markingNoEvidence}
              className={`
                inline-flex items-center gap-1 px-2.5 py-1 rounded-full
                text-xs font-medium font-circular transition-colors
                ${
                  noSelected
                    ? "bg-[#D6E0F8] text-primary500 ring-1 ring-primary500"
                    : requiredUnanswered
                    ? "bg-[#F3F5FB] text-padeBlack ring-1 ring-[#F59E0B] hover:bg-[#EEF0F7]"
                    : "bg-[#F3F5FB] text-padeBlack hover:bg-[#EEF0F7]"
                }
              `}
            >
              <XMarkIcon className="w-3.5 h-3.5" /> No
            </button>
          </Tooltip>
        </div>
      </div>

      {open && <Suspense fallback={null}><KeyResultEvidenceDrawer
        open={open}
        onClose={() => setOpen(false)}
        keyResultId={keyResultId}
        reviewPeriodId={reviewPeriodId ?? 0}
        keyResultTitle={keyResultTitle}
        role={role}
        viewType={viewType}
        requireEvidence={requireEvidence}
        onEvidenceChange={handleDrawerSave}
      /></Suspense>}
    </>
  )
}

export default KeyResultEvidenceButton
